/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import PropTypes from "prop-types";
import dayjs from "dayjs";

const cardSx = {
  backgroundColor: "#F5F5F5",
  borderRadius: "4px",
  p: "1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
};

const BillingPeriodsCard = ({ calendarDetail, dates = [], isLoading = false }) => {
  if (isLoading) {
    return (
      <Box sx={{ ...cardSx, justifyContent: "center", alignItems: "center", minHeight: "202px" }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (!calendarDetail) return null;

  const periodEndDates = dates
    .filter((d) => d.startDateTime)
    .map((d) => dayjs(d.startDateTime).format("MMM D"))
    .join(", ");

  const seededUntil = calendarDetail.events?.[0]?.seededUntil;

  return (
    <Box sx={cardSx}>
      <Box>
        <Typography variant="h6">
          {calendarDetail.calendarName || `Calendar ${calendarDetail.calendarId}`}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Period End Dates
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
          {periodEndDates || (dates.length === 0 ? "No dates available" : "—")}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          Updated: {dayjs().format("MMM D, YYYY")}
        </Typography>
        {seededUntil && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Seeded until: {dayjs(seededUntil).format("MMM D, YYYY")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

BillingPeriodsCard.propTypes = {
  calendarDetail: PropTypes.shape({
    calendarId: PropTypes.number,
    calendarName: PropTypes.string,
    events: PropTypes.array,
  }),
  dates: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default BillingPeriodsCard;
